'use client'

import React, { createContext, useContext } from 'react'

import * as TabsPrimitive from '@radix-ui/react-tabs'
import { twMerge } from 'tailwind-merge'

type Variant = 'ghost' | 'grouped' | 'underline'

interface VariantProps {
  variant?: Variant
}

interface TabsContextValue {
  variant: Variant
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined)

const useTabsContext = () => {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('useTabsContext must be used within a TabsProvider')
  }
  return context
}

const useTabsVariant = (props: VariantProps) => {
  const { variant } = useTabsContext()
  return props.variant ?? variant
}

const TabsProvider: React.FC<
  TabsContextValue & { children: React.ReactNode }
> = ({ variant, children }) => {
  return (
    <TabsContext.Provider value={{ variant }}>{children}</TabsContext.Provider>
  )
}

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & VariantProps
>(({ variant = 'ghost', ...props }, ref) => (
  <TabsProvider variant={variant}>
    <TabsPrimitive.Root ref={ref} {...props} />
  </TabsProvider>
))
Tabs.displayName = TabsPrimitive.Root.displayName

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & VariantProps
>(({ className, variant: propsVariant, ...props }, ref) => {
  const variant = useTabsVariant({ variant: propsVariant })
  return (
    <TabsPrimitive.List
      ref={ref}
      className={twMerge(
        'inline-flex items-center justify-center text-primary-900 dark:text-primary-100',
        variant === 'grouped' &&
          'w-full bg-primary-100 dark:bg-inherit border border-primary-200 dark:border-primary-800 rounded-md',
        variant === 'ghost' && 'gap-1 rounded-md',
        variant === 'underline' && 'gap-5',
        className,
      )}
      {...props}
    />
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & VariantProps
>(({ className, ...props }, ref) => {
  const variant = useTabsVariant(props)
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={twMerge(
        'inline-flex items-center justify-center whitespace-nowrap',
        'font-medium transition-colors duration-75',
        variant === 'ghost' &&
          'text-sm px-2 h-7 rounded-[5px] text-primary-900 dark:text-primary-100',
        variant === 'grouped' &&
          'text-[12px] px-2 h-6 rounded-[4px] my-[1px] first:ml-[1px] last:mr-[1px] w-full text-primary-700 dark:text-primary-300 border border-transparent',
        variant === 'underline' &&
          'text-sm h-12 px-0 -mb-px border-b-2 border-transparent text-primary-500 dark:text-primary-400',
        props.disabled
          ? 'disabled:opacity-50 disabled:cursor-not-allowed'
          : variant === 'ghost'
            ? 'hover:bg-primary-200 dark:hover:bg-primary-800'
            : variant === 'underline'
              ? 'hover:text-primary-950 dark:hover:text-primary-50'
              : 'hover:text-primary-900 dark:hover:text-primary-100',
        variant === 'ghost' &&
          'data-[state=active]:bg-primary-200 dark:data-[state=active]:bg-primary-800',
        variant === 'grouped' &&
          'data-[state=active]:bg-white dark:data-[state=active]:bg-primary-800',
        variant === 'underline' &&
          'data-[state=active]:border-primary-950 data-[state=active]:text-primary-950 dark:data-[state=active]:border-primary-50 dark:data-[state=active]:text-primary-50',
        className,
      )}
      {...props}
    />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & VariantProps
>(({ className, ...props }, ref) => {
  const variant = useTabsVariant(props)
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={twMerge('mt-2', className)}
      {...props}
    />
  )
})
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }

/** A pill trigger for a ghost tab row, filled when active: every chat panel view with tabs uses it. */
export const PILL_TAB_TRIGGER =
  'h-8 px-3 text-primary-500 hover:bg-primary-50 hover:text-primary-900 dark:hover:bg-primary-900 data-[state=active]:bg-primary-100 data-[state=active]:text-primary-900 dark:text-primary-400 dark:data-[state=active]:bg-primary-800 dark:data-[state=active]:text-primary-100'
